import { ID, Query, type Models } from "appwrite";
import { databases } from "@/lib/appwrite";
import type { VacationRequest, VacationRequestStatus } from "./types";
import { fromAppwriteDatetime, toAppwriteDatetime } from "./vacationBalanceService";


const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const COLLECTION_ID = import.meta.env.VITE_VACATION_REQUESTS_COLLECTION_ID;

type RequestDoc = Models.Document &
    Omit<VacationRequest, "$id" | "startDate" | "endDate"> & {
        startDate: string;
        endDate: string;
    };

const toRequest = (document: RequestDoc): VacationRequest => ({
    $id: document.$id,
    $createdAt: document.$createdAt,
    userId: document.userId,
    startDate: fromAppwriteDatetime(document.startDate),
    endDate: fromAppwriteDatetime(document.endDate),
    calendarDays: document.calendarDays,
    daysDeducted: document.daysDeducted,
    status: document.status,
    reason: document.reason ?? null,
    reviewedById: document.reviewedById ?? null,
    reviewedAt: document.reviewedAt ?? null,
    rejectionReason: document.rejectionReason ?? null,
});

const ACTIVE_STATUSES: VacationRequestStatus[] = ["pending", "approved"];

export type NewVacationRequest = Pick<
    VacationRequest,
    "userId" | "startDate" | "endDate" | "calendarDays" | "daysDeducted" | "reason"
>;

export async function createVacationRequest(input: NewVacationRequest): Promise<VacationRequest> {

    const overlapping = await databases.listDocuments<RequestDoc>(DATABASE_ID, COLLECTION_ID, [
        Query.equal("userId", input.userId),
        Query.equal("status", ACTIVE_STATUSES),
        Query.lessThanEqual("startDate", toAppwriteDatetime(input.endDate)),
        Query.greaterThanEqual("endDate", toAppwriteDatetime(input.startDate)),
        Query.limit(1),
    ]);
    if (overlapping.total > 0) throw new Error("OVERLAP");

    const doc = await databases.createDocument<RequestDoc>(DATABASE_ID, COLLECTION_ID, ID.unique(), {
        userId: input.userId,
        startDate: toAppwriteDatetime(input.startDate),
        endDate: toAppwriteDatetime(input.endDate),
        calendarDays: input.calendarDays,
        daysDeducted: input.daysDeducted,
        status: "pending",
        reason: input.reason?.trim() || null,
    });
    return toRequest(doc);
}

export async function getRequestsByUser(userId: string): Promise<VacationRequest[]> {
    const response = await databases.listDocuments<RequestDoc>(DATABASE_ID, COLLECTION_ID, [
        Query.equal("userId", userId),
        Query.orderDesc("startDate"),
        Query.limit(100),
    ]);
    return response.documents.map(toRequest);
}

// Días ya comprometidos: solicitudes aprobadas (incluye las futuras que aún no se toman)
export async function getTakenDays(userId: string): Promise<number> {
    const response = await databases.listDocuments<RequestDoc>(DATABASE_ID, COLLECTION_ID, [
        Query.equal("userId", userId),
        Query.equal("status", "approved"),
        Query.limit(500),
    ]);
    return response.documents.reduce((sum, document) => sum + document.daysDeducted, 0);
}

export async function approveVacationRequest(id: string, reviewerId: string): Promise<void> {
    await assertPending(id);
    await databases.updateDocument(DATABASE_ID, COLLECTION_ID, id, {
        status: "approved",
        reviewedById: reviewerId,
        reviewedAt: new Date().toISOString(),
    });
}

export async function rejectVacationRequest(
    id: string,
    reviewerId: string,
    rejectionReason: string
): Promise<void> {
    await assertPending(id);
    await databases.updateDocument(DATABASE_ID, COLLECTION_ID, id, {
        status: "rejected",
        reviewedById: reviewerId,
        reviewedAt: new Date().toISOString(),
        rejectionReason: rejectionReason.trim() || null,
    });
}

export async function cancelVacationRequest(id: string): Promise<void> {
    await assertPending(id);
    await databases.updateDocument(DATABASE_ID, COLLECTION_ID, id, { status: "cancelled" });
}


export async function getRequestsByStatus(
    status: VacationRequestStatus
): Promise<VacationRequest[]> {
    const response = await databases.listDocuments<RequestDoc>(DATABASE_ID, COLLECTION_ID, [
        Query.equal("status", status),
        // Las pendientes: primero las que empiezan más pronto. Las demás: las más recientes primero.
        status === "pending" ? Query.orderAsc("startDate") : Query.orderDesc("startDate"),
        Query.limit(100),
    ]);
    return response.documents.map(toRequest);
}

// Solo se puede actuar sobre solicitudes que siguen pendientes
async function assertPending(id: string): Promise<void> {
    const current = await databases.getDocument<RequestDoc>(DATABASE_ID, COLLECTION_ID, id);
    if (current.status !== "pending") throw new Error("NOT_PENDING");
}